import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import {
    Badge,
    Box,
    Button,
    Card,
    Center,
    Group,
    SegmentedControl,
    Stack,
    Text,
    TextInput,
    ThemeIcon,
    Title,
} from '@mantine/core'
import {
    IconAlertTriangle,
    IconMessages,
    IconPlus,
    IconSearch,
} from '@tabler/icons-react'
import { formatDistanceToNow } from 'date-fns'
import { CtaButton } from '@/components/CtaButton'
import { EmptyState } from '@/components/EmptyState'
import { AttentionBanner } from '@/components/AttentionBanner'
import { ListSkeleton } from '@/components/ListSkeleton'
import { DeleteButton } from '@/components/DeleteButton'
import { RowActionButton } from '@/components/RowActionButton'
import { segmentKey } from '@/features/segment/api'
import { useSegmentStubs } from '@/features/segment/queries'
import { useCreateNewAudience } from './useCreateNewAudience'
import { useGetThreads, useDeleteThread } from './queries'
import type { ThreadInfo } from './api'
import classes from './ThreadsSummaryPage.module.css'

// A thread's re-entry state (navigation-ia.md §2): exploring → ready →
// delivered, with problems (a failed / blocked distribution) surfaced hardest.
type ThreadState = 'attention' | 'ready' | 'exploring' | 'delivered'

const STATE_META: Record<
    ThreadState,
    { label: string; color: string; rank: number }
> = {
    attention: { label: 'Needs attention', color: 'warning', rank: 0 },
    ready: { label: 'Ready to distribute', color: 'info', rank: 1 },
    exploring: { label: 'Exploring', color: 'gray', rank: 2 },
    delivered: { label: 'Delivered', color: 'success', rank: 3 },
}

interface ThreadRow {
    thread: ThreadInfo
    state: ThreadState
    segmentCount: number
    liveCount: number
}

// The segment-based view filter. "No segments" is the set the dashboard calls
// "Exploring" (a thread with no segments yet), so the Exploring pill deep-links
// here via ?view=no-segments.
type ThreadView = 'all' | 'has-segments' | 'no-segments'

function parseView(value: string | null): ThreadView {
    return value === 'has-segments' || value === 'no-segments' ? value : 'all'
}

export function ThreadsSummaryPage() {
    const { createNewAudience } = useCreateNewAudience()
    const {
        data: threads,
        isLoading,
        isError,
        isFetching,
        refetch,
    } = useGetThreads()
    const { mutateAsync: deleteThread, isPending: isPendingDeleteThread } =
        useDeleteThread()
    const { lifecycleFor } = useSegmentStubs()

    const [query, setQuery] = useState('')
    // View filter is URL-driven (?view=…) so the dashboard's "Exploring" pill can
    // deep-link straight to the no-segments slice.
    const [searchParams, setSearchParams] = useSearchParams()
    const view = parseView(searchParams.get('view'))
    const setView = (value: ThreadView) =>
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                if (value === 'all') next.delete('view')
                else next.set('view', value)
                return next
            },
            { replace: true }
        )

    // Derive each thread's state from its real segments + the stub lifecycle,
    // then sort "needs you" first, recency within.
    const rows = useMemo<ThreadRow[]>(() => {
        if (!threads) return []
        const computed = threads.map((t): ThreadRow => {
            const segments = t.state.audienceConfigurations ?? []
            const liveDestIds = new Set<string>()
            let hasAttention = false
            for (const config of segments) {
                const life = lifecycleFor(segmentKey(config), t.threadId)
                if (life.distributeStatus === 'attention') hasAttention = true
                for (const delivery of life.deliveries) {
                    const status = delivery.latestRun?.status
                    if (status === 'succeeded' || status === 'partial') {
                        liveDestIds.add(delivery.distribution.destinationId)
                    }
                }
            }
            const state: ThreadState =
                segments.length === 0
                    ? 'exploring'
                    : hasAttention
                      ? 'attention'
                      : liveDestIds.size > 0
                        ? 'delivered'
                        : 'ready'
            return {
                thread: t,
                state,
                segmentCount: segments.length,
                liveCount: liveDestIds.size,
            }
        })
        return computed.sort((a, b) => {
            const rank = STATE_META[a.state].rank - STATE_META[b.state].rank
            if (rank !== 0) return rank
            return (
                Date.parse(b.thread.lastUpdated) -
                Date.parse(a.thread.lastUpdated)
            )
        })
    }, [threads, lifecycleFor])

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        return rows.filter((r) => {
            if (view === 'no-segments' && r.segmentCount > 0) return false
            if (view === 'has-segments' && r.segmentCount === 0) return false
            if (
                q &&
                !(r.thread.state.threadTitle ?? '').toLowerCase().includes(q)
            ) {
                return false
            }
            return true
        })
    }, [rows, query, view])

    const noSegmentCount = rows.filter((r) => r.segmentCount === 0).length

    return (
        <Stack gap='lg' h='100%'>
            <Group
                justify='space-between'
                align='flex-start'
                style={{ flexShrink: 0 }}
            >
                <div>
                    <Title order={2}>
                        <IconMessages
                            size={24}
                            style={{ verticalAlign: 'text-bottom' }}
                        />{' '}
                        Threads
                    </Title>
                    <Text c='dimmed'>
                        Your conversations with the agent — pick up where you
                        left off; the ones that need you come first.
                    </Text>
                </div>
                <Group gap='sm'>
                    <TextInput
                        placeholder='Search threads'
                        aria-label='Search threads'
                        leftSection={<IconSearch size={16} />}
                        value={query}
                        onChange={(e) => setQuery(e.currentTarget.value)}
                        w={220}
                    />
                    <CtaButton
                        leftSection={<IconPlus size={16} />}
                        onClick={createNewAudience}
                    >
                        New thread
                    </CtaButton>
                </Group>
            </Group>

            {isLoading ? (
                <ListSkeleton height={84} />
            ) : isError ? (
                <Card withBorder style={{ flex: 1 }}>
                    <Center h='100%'>
                        <Stack align='center' gap='xs' py='xl' maw={440}>
                            <ThemeIcon
                                variant='light'
                                color='error'
                                size={56}
                                radius='md'
                            >
                                <IconAlertTriangle size={28} />
                            </ThemeIcon>
                            <Text fw={600}>Unable to load your threads</Text>
                            <Text c='dimmed' size='sm' ta='center'>
                                Something went wrong fetching them. Please try
                                again.
                            </Text>
                            <Button
                                variant='light'
                                mt='xs'
                                loading={isFetching}
                                onClick={() => void refetch()}
                            >
                                Retry
                            </Button>
                        </Stack>
                    </Center>
                </Card>
            ) : rows.length === 0 ? (
                <EmptyState
                    icon={<IconMessages size={28} />}
                    title='No threads yet'
                    description='Start a conversation with the agent to ideate a strategy and build your first audience.'
                    action={
                        <CtaButton
                            leftSection={<IconPlus size={16} />}
                            mt='xs'
                            onClick={createNewAudience}
                        >
                            New thread
                        </CtaButton>
                    }
                />
            ) : (
                <>
                    {noSegmentCount > 0 && view !== 'no-segments' && (
                        <AttentionBanner
                            title={`${noSegmentCount} thread${noSegmentCount === 1 ? '' : 's'} ${noSegmentCount === 1 ? 'has' : 'have'} no segments yet`}
                            onAction={() => setView('no-segments')}
                            actionLabel='Show these'
                        />
                    )}

                    <SegmentedControl
                        value={view}
                        onChange={(v) => setView(v as ThreadView)}
                        data={[
                            { value: 'all', label: 'All threads' },
                            { value: 'has-segments', label: 'Has segments' },
                            { value: 'no-segments', label: 'No segments' },
                        ]}
                        style={{ flexShrink: 0, alignSelf: 'flex-start' }}
                    />

                    <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        <Stack gap='sm'>
                            {filtered.length === 0 ? (
                                <Text c='dimmed' ta='center' py='xl'>
                                    No threads match these filters.
                                </Text>
                            ) : (
                                filtered.map((row) => (
                                    <ThreadCard
                                        key={row.thread.threadId}
                                        row={row}
                                        onDelete={() =>
                                            deleteThread(row.thread.threadId)
                                        }
                                        deleting={isPendingDeleteThread}
                                    />
                                ))
                            )}
                        </Stack>
                    </Box>
                </>
            )}
        </Stack>
    )
}

// "What came of it" — the badge already conveys state, so the subline stays
// informational: segments built and where they're delivered.
function deliveryText(row: ThreadRow): string {
    const { segmentCount, liveCount, state } = row
    if (state === 'exploring' || segmentCount === 0) return 'No segments yet'
    const seg = `${segmentCount} segment${segmentCount === 1 ? '' : 's'}`
    if (liveCount > 0) {
        return `${seg} · live on ${liveCount} destination${liveCount === 1 ? '' : 's'}`
    }
    return `${seg} · not yet distributed`
}

function ThreadCard({
    row,
    onDelete,
    deleting,
}: {
    row: ThreadRow
    onDelete: () => void | Promise<void>
    deleting: boolean
}) {
    const meta = STATE_META[row.state]
    return (
        <Card withBorder radius='md' padding='md' className={classes.card}>
            <Group justify='space-between' wrap='nowrap' align='center'>
                <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap='xs' wrap='nowrap'>
                        {/* Real link, stretched over the whole card via ::after
                            (module CSS) — the card is clickable but focus and
                            link semantics live on this anchor. */}
                        <Text
                            component={Link}
                            to={`/thread/${row.thread.threadId}`}
                            className={`${classes.titleLink} mantine-focus-auto`}
                            fw={600}
                            truncate
                            style={{ minWidth: 0 }}
                        >
                            {row.thread.state.threadTitle || 'Untitled thread'}
                        </Text>
                        <Badge size='sm' variant='light' color={meta.color}>
                            {meta.label}
                        </Badge>
                    </Group>
                    <Text size='sm' c='dimmed'>
                        {row.segmentCount === 0 ? (
                            <Text span c='warning'>
                                No segments yet
                            </Text>
                        ) : (
                            deliveryText(row)
                        )}{' '}
                        · updated{' '}
                        {formatDistanceToNow(new Date(row.thread.lastUpdated), {
                            addSuffix: true,
                        })}
                    </Text>
                </Stack>
                <Group gap='xs' wrap='nowrap' className={classes.actions}>
                    {/* Distribute is only meaningful once the thread has built
                        segments; it deep-links to the audience (expanded, with
                        its Distribute action focused). */}
                    {row.segmentCount > 0 && (
                        <RowActionButton
                            to={`/audiences?distribute=${row.thread.threadId}`}
                            label='Distribute'
                        />
                    )}
                    {/* size=md (≥24px) so the target clears WCAG 2.5.8 and is
                        harder to hit by accident on the full-width row link. */}
                    <DeleteButton
                        onDelete={onDelete}
                        isLoading={deleting}
                        size='md'
                        title='Delete thread'
                        confirmTitle='Delete thread'
                        confirmMessage='Are you sure you want to delete this thread? All messages and data will be permanently lost.'
                    />
                </Group>
            </Group>
        </Card>
    )
}
